import { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import {
  type CreateStepRequest,
  type PlanStep,
  type UpdateStepRequest,
  useAddStep,
  useCompleteStep,
  useDeleteStep,
  useNetworkPlan,
  useReorderStep,
  useRetryStep,
  useSkipStep,
  useUpdateStep,
} from '../hooks/useNetworkPlans';
import { useDevices } from '../hooks/useDevices';
import { useConfirm, usePrompt } from '../hooks/useConfirm';
import PlanStepList from './PlanStepList';
import AddStepModal from './AddStepModal';
import { AppSheet } from '@/components/ui/app-sheet';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';

interface PlanStepEditorProps {
  planId: string;
  className?: string;
}

/**
 * Self-contained step editor for a single network plan: the step list plus the
 * add/edit and complete/fail modals and every step-level mutation. It derives
 * editability from the plan's own state (draft = editable, executing = live
 * manual-step actions), so it works identically embedded in a CM detail page or
 * on the standalone plan page — there's one source of truth for step editing.
 */
export default function PlanStepEditor({ planId, className }: PlanStepEditorProps) {
  const { data: plan, isLoading } = useNetworkPlan(planId);

  const { data: devices } = useDevices(plan?.organizationId);
  const deviceNames = useMemo(() => {
    if (!devices) return {};
    const map: Record<string, string> = {};
    for (const d of devices) map[d.id] = d.name;
    return map;
  }, [devices]);

  const addStepMutation = useAddStep();
  const updateStepMutation = useUpdateStep();
  const deleteStepMutation = useDeleteStep();
  const reorderStepMutation = useReorderStep();
  const completeStepMutation = useCompleteStep();
  const skipStepMutation = useSkipStep();
  const retryStepMutation = useRetryStep();

  const [isStepModalActive, setIsStepModalActive] = useState(false);
  const [editingStep, setEditingStep] = useState<PlanStep | null>(null);

  const [isCompleteModalActive, setIsCompleteModalActive] = useState(false);
  const [completingStepId, setCompletingStepId] = useState<string | null>(null);
  const [completeNotes, setCompleteNotes] = useState('');
  const { confirm, confirmDialog } = useConfirm();
  const { prompt, promptDialog } = usePrompt();

  const isDraft = plan?.state === 'draft';
  const isExecuting = plan?.state === 'executing';

  const openAddStepModal = () => {
    setEditingStep(null);
    setIsStepModalActive(true);
  };

  const openEditStepModal = (step: PlanStep) => {
    setEditingStep(step);
    setIsStepModalActive(true);
  };

  const handleStepSave = async (data: CreateStepRequest | UpdateStepRequest, stepId?: string) => {
    if (stepId) {
      await updateStepMutation.mutateAsync({ planId, stepId, data });
    } else {
      await addStepMutation.mutateAsync({ planId, data: data as CreateStepRequest });
    }
    setIsStepModalActive(false);
  };

  const handleDeleteStep = async (stepId: string) => {
    const ok = await confirm({
      title: 'Delete step',
      message: 'Delete this step? This cannot be undone.',
      confirmText: 'Delete',
      isDanger: true,
    });
    if (!ok) return;
    try {
      await deleteStepMutation.mutateAsync({ planId, stepId });
    } catch {
      // ignore
    }
  };

  const handleMoveUp = (stepId: string) => {
    if (!plan) return;
    const step = plan.steps.find((s) => s.id === stepId);
    if (!step || step.position <= 0) return;
    reorderStepMutation.mutate({ planId, stepId, data: { newPosition: step.position - 1 } });
  };

  const handleMoveDown = (stepId: string) => {
    if (!plan) return;
    const step = plan.steps.find((s) => s.id === stepId);
    if (!step || step.position >= plan.steps.length - 1) return;
    reorderStepMutation.mutate({ planId, stepId, data: { newPosition: step.position + 1 } });
  };

  const handleCompleteStep = (stepId: string) => {
    setCompletingStepId(stepId);
    setCompleteNotes('');
    setIsCompleteModalActive(true);
  };

  const handleCompleteSubmit = (success: boolean) => {
    if (!completingStepId) return;
    completeStepMutation.mutate(
      { planId, stepId: completingStepId, data: { notes: completeNotes || undefined, success } },
      { onSuccess: () => { setIsCompleteModalActive(false); } },
    );
  };

  const handleSkipStep = async (stepId: string) => {
    const reason = await prompt({
      title: 'Skip step',
      message: 'Reason for skipping (optional):',
      confirmText: 'Skip step',
    });
    if (reason === null) return;
    skipStepMutation.mutate({ planId, stepId, data: { reason: reason || undefined } });
  };

  const handleRetryStep = (stepId: string) => {
    retryStepMutation.mutate({ planId, stepId });
  };

  if (isLoading || !plan) {
    return <Skeleton className="h-16 w-full" />;
  }

  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold">
          Steps ({plan.completedStepCount}/{plan.stepCount})
        </span>
        {isDraft ? (
          <Button size="sm" onClick={openAddStepModal} className="gap-1.5">
            <Plus className="w-4 h-4" />
            Add step
          </Button>
        ) : null}
      </div>

      <PlanStepList
        steps={plan.steps}
        planId={planId}
        editable={isDraft}
        executing={isExecuting}
        deviceNames={deviceNames}
        onEdit={openEditStepModal}
        onDelete={(stepId) => void handleDeleteStep(stepId)}
        onMoveUp={handleMoveUp}
        onMoveDown={handleMoveDown}
        onComplete={handleCompleteStep}
        onSkip={(stepId) => void handleSkipStep(stepId)}
        onRetry={handleRetryStep}
      />

      <AddStepModal
        isActive={isStepModalActive}
        onClose={() => { setIsStepModalActive(false); }}
        onSave={handleStepSave}
        editingStep={editingStep}
        organizationId={plan.organizationId}
        isSaving={addStepMutation.isPending || updateStepMutation.isPending}
      />

      <AppSheet
        open={isCompleteModalActive}
        onOpenChange={(open) => { if (!open) setIsCompleteModalActive(false); }}
        title="Complete step"
        size="md"
      >
        <AppSheet.Body>
          <div className="space-y-1.5">
            <Label htmlFor="cs-notes">Notes (optional)</Label>
            <Textarea
              id="cs-notes"
              value={completeNotes}
              onChange={(e) => { setCompleteNotes(e.target.value); }}
              placeholder="Any notes about the completion of this step"
              rows={3}
            />
          </div>
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button
            variant="outline"
            onClick={() => { setIsCompleteModalActive(false); }}
            disabled={completeStepMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            className="text-sev-1 hover:text-sev-1"
            onClick={() => { handleCompleteSubmit(false); }}
            disabled={completeStepMutation.isPending}
          >
            Mark failed
          </Button>
          <Button
            onClick={() => { handleCompleteSubmit(true); }}
            disabled={completeStepMutation.isPending}
          >
            {completeStepMutation.isPending ? 'Saving…' : 'Mark complete'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>

      {confirmDialog}
      {promptDialog}
    </div>
  );
}
